import type { CSSProperties } from 'react';
import { CUSTOM_CHART_SIZES, type CustomChartSize } from '@/entities/dashboards';
import { DASHBOARD_BLOCK_MAX_HEIGHT, cn } from '@/shared/lib';
import { dashboardGridHeight } from '../utils';

/**
 * Класс ячейки, начинающей собой строку сетки: ячейка встаёт в первую колонку, и не поместившийся
 * перед ней остаток предыдущей строки остаётся незанятым.
 */
export const CHART_ROW_START_CLASS = 'lg:col-start-1';

/**
 * Доля ширины сетки в колонках раскладки. Сетка из 60 колонок делится нацело на все доли
 * каталога (1/5, 1/4, 1/3, 1/2, во всю ширину), поэтому диаграммы встают без остатка.
 * На узком экране сетка одноколоночная — доля ширины там ничего не решает.
 */
export const CHART_SIZE_COL_SPAN: Record<CustomChartSize, string> = {
  '1/5': 'lg:col-span-12',
  '1/4': 'lg:col-span-15',
  '1/3': 'lg:col-span-20',
  '1/2': 'lg:col-span-30',
  full: 'lg:col-span-60',
};

/**
 * Классы сетки дашборда: одна колонка на узком экране, 60 долей — на широком.
 *
 * Зазор между диаграммами задают не промежутки сетки, а отступы самих ячеек ({@link
 * CHART_CELL_GUTTER_CLASS}) — по обеим сторонам. По ширине: 59 промежутков между 60 долями требуют
 * ширины больше той, что остаётся странице на невеликом экране, и сетка вылезала бы за неё вместе с
 * правыми диаграммами; отступ внутри ячейки долю ширины не занимает, поэтому сетка укладывается в
 * любую ширину. По высоте зазор задан тем же отступом: он один и у диаграмм, и у блоков, что встают
 * в сетку между строками, — места под новую диаграмму и развёрнутых свойств.
 *
 * Отступ ячеек сетка возвращает себе полями наружу — иначе крайние диаграммы отступали бы от краёв
 * страницы, а соседние с ними блоки — нет.
 */
export const CHART_GRID_CLASS = 'grid grid-cols-1 -my-2 lg:-mx-2 lg:grid-cols-60';

/**
 * Отступ ячейки сетки: половина зазора с каждой стороны — вместе с соседкой выходит целый.
 *
 * По ширине это отступ внутри ячейки, по высоте — поле снаружи: высота ячейки задана высотой
 * диаграммы, и внутренний отступ отнял бы её у самой диаграммы.
 */
export const CHART_CELL_GUTTER_CLASS = 'my-2 lg:px-2';

/** Ширина сетки дашборда в колонках раскладки. */
const CHART_GRID_COLUMNS = 60;

/** Колонки раскладки, которые занимает диаграмма каждой доли ширины каталога. */
export const CHART_SIZE_COLUMNS: Record<CustomChartSize, number> = {
  '1/5': 12,
  '1/4': 15,
  '1/3': 20,
  '1/2': 30,
  full: 60,
};

/** Диаграмма глазами раскладки: доля ширины и признак начала строки. */
export type ChartGridPlacement = {
  size: CustomChartSize;
  /** Диаграмма начинает собой строку — предыдущая строка обрывается на соседке слева */
  startsRow?: boolean;
};

/** Строка сетки дашборда: какие диаграммы в неё встали и сколько ширины в ней осталось. */
export type ChartGridRow = {
  /** Позиции диаграмм строки в составе дашборда */
  chartIndexes: number[];
  /** Незанятый конец строки в колонках раскладки */
  freeColumns: number;
};

/**
 * Раскладывает состав по строкам так же, как его раскладывает сетка: диаграммы идут подряд, не
 * поместившаяся в остаток строки переходит на следующую, а помеченная началом строки открывает
 * новую сама.
 */
export const chartGridRows = (charts: ChartGridPlacement[]): ChartGridRow[] => {
  const rows: ChartGridRow[] = [];

  charts.forEach((chart, index) => {
    const columns = CHART_SIZE_COLUMNS[chart.size];
    const currentRow = rows.at(-1);
    if (!currentRow || chart.startsRow || columns > currentRow.freeColumns) {
      rows.push({ chartIndexes: [index], freeColumns: CHART_GRID_COLUMNS - columns });
      return;
    }
    currentRow.chartIndexes.push(index);
    currentRow.freeColumns -= columns;
  });

  return rows;
};

/** Наименьшая доля ширины каталога в колонках раскладки — уже неё диаграмма не бывает. */
const MIN_CHART_COLUMNS = Math.min(...CUSTOM_CHART_SIZES.map((size) => CHART_SIZE_COLUMNS[size]));

/**
 * В незанятый конец строки помещается ещё одна диаграмма. По этому и показывается место под
 * новую: остаток уже наименьшей доли заводить в него нечем.
 */
export const hasRoomForChart = (freeColumns: number): boolean => freeColumns >= MIN_CHART_COLUMNS;

/** Зазор между ячейками сетки в пикселях — тот же, что складывается из {@link CHART_CELL_GUTTER_CLASS}. */
const CHART_GRID_GAP = 16;

/** Доля ширины сетки, которую занимает диаграмма этого размера каталога. */
const chartSizeRatio = (size: CustomChartSize): number => CHART_SIZE_COLUMNS[size] / CHART_GRID_COLUMNS;

/**
 * Ближайшая к растянутой ширине доля каталога: ширина диаграммы остаётся долей ширины сетки,
 * промежуточных значений диаграмма не принимает.
 *
 * Доля считается с поправкой на зазор: карточка стоит внутри отступов своей ячейки и уже её на
 * целый зазор, и без поправки растянутая ровно до половины диаграмма встаёт на 1/3.
 */
export const nearestChartSize = (widthPx: number, gridWidthPx: number): CustomChartSize => {
  if (gridWidthPx <= 0) return 'full';
  const ratio = (widthPx + CHART_GRID_GAP) / gridWidthPx;
  return CUSTOM_CHART_SIZES.reduce((nearest, size) =>
    Math.abs(chartSizeRatio(size) - ratio) < Math.abs(chartSizeRatio(nearest) - ratio) ? size : nearest,
  );
};

/** Наименьшая высота диаграммы: ниже неё не помещается даже заголовок с одной строкой данных. */
export const MIN_CHART_HEIGHT = 160;

/** Та же наименьшая высота стилем — для блоков сетки, которым её задаёт не растягивание. */
export const MIN_CHART_HEIGHT_STYLE: CSSProperties = { minHeight: MIN_CHART_HEIGHT };

/** Заданная растягиванием высота — в границах от {@link MIN_CHART_HEIGHT} до потолка блоков дашборда. */
export const clampChartHeight = (heightPx: number): number =>
  Math.min(Math.max(Math.round(heightPx), MIN_CHART_HEIGHT), DASHBOARD_BLOCK_MAX_HEIGHT);

/**
 * Ячейка не ниже своего содержимого, когда в нём место таблицы с легендой под ней (признак
 * `data-keeps-table` у `ChartCardShell`). Легенда видна целиком, и у наименьших заданных высот
 * таблице над ней осталась бы одна шапка — место добавляет сама диаграмма: заданная ниже высота
 * показывается не ниже той, в которую входят заголовок, наименьшая таблица и легенда.
 *
 * Высоту содержимого ячейке передаёт её прямой потомок, и только пока его наименьшая высота не
 * обнулена и он не прокручиваемый: обрезка `clip` держит содержимое в границах так же, как
 * `hidden`, но наименьшую высоту не гасит.
 */
const CHART_CELL_KEEPS_TABLE_CLASS =
  'has-[[data-keeps-table]]:min-h-min has-[[data-keeps-table]]:*:min-h-auto has-[[data-keeps-table]]:*:overflow-clip';

/**
 * Классы ячейки сетки под диаграмму: доля ширины и отступ, дающий зазор с соседками. Ячейка —
 * сетка из одного элемента: карточка диаграммы растягивается по ней до заданной высоты.
 */
export const chartCellClass = (size: CustomChartSize, startsRow?: boolean): string =>
  cn(
    'grid min-w-0',
    CHART_SIZE_COL_SPAN[size],
    CHART_CELL_GUTTER_CLASS,
    CHART_CELL_KEEPS_TABLE_CLASS,
    startsRow && CHART_ROW_START_CLASS,
  );

/**
 * Классы карточки внутри ячейки: карточка занимает отведённые диаграмме ширину и высоту и за
 * них не выходит.
 *
 * Прокрутки на карточке нет: прокручивается содержимое диаграммы внутри неё, поэтому полоса
 * появляется только по той стороне, где содержимому не хватило места, и не идёт поверх заголовка.
 */
export const CHART_CARD_CLASS = 'grid min-h-0 min-w-0 overflow-hidden';

/** Высота ячейки диаграммы; без заданной высоты ячейка тянется по содержимому. */
export const chartCellStyle = (height?: number | null): CSSProperties | undefined => (height ? { height } : undefined);

/**
 * Высота таблицы внутри диаграммы: у диаграммы с заданной высотой таблица занимает остаток
 * карточки над легендой и прокручивает строки сама, иначе тянется по своим строкам в пределах
 * потолка блоков дашборда.
 */
export const chartTableHeight = (rowCount: number, hasFixedHeight?: boolean): number | string =>
  hasFixedHeight ? '100%' : dashboardGridHeight(rowCount);

/**
 * Наименьшая высота таблицы внутри диаграммы — её шапка, итог и хотя бы одна строка выборки.
 *
 * Заданная высота бывает и меньше: у наименьших высот на шапку с итогом уходит всё место, и
 * строкам не остаётся ни одной точки. Таблица тогда встаёт этой наименьшей высотой, а не
 * поместившееся прокручивается в отведённом таблице месте — выборка остаётся достижимой при любой
 * заданной высоте.
 */
const CHART_TABLE_MIN_HEIGHT = dashboardGridHeight(1);

/**
 * Стиль таблицы внутри диаграммы и места таблицы с легендой под ним: заданная высота не отнимает у
 * строк место под них. Место с легендой не уже наименьшей таблицы, иначе прокрутка этого места
 * показывала бы шапку и итог порознь, а строку — ни разу целиком.
 */
export const chartTableStyle = (hasFixedHeight?: boolean): CSSProperties | undefined =>
  hasFixedHeight ? { minHeight: CHART_TABLE_MIN_HEIGHT } : undefined;
